import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatApiError, formatDate, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoLight } from "@/components/site/Logo";
import { NewsEditor } from "@/components/admin/NewsEditor";
import { AdminMessages } from "@/components/admin/AdminMessages";

const TABS = [{ k: "news", t: "Nyheter" }, { k: "messages", t: "Inkomna meddelanden" }];

const Row = ({ n, onEdit, onDelete }) => (
  <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-[#E3E2DA]" data-testid={`admin-news-row-${n.id}`}>
    <div className="w-20 h-14 sm:w-28 sm:h-16 shrink-0 rounded-lg overflow-hidden bg-[#070B2E]">
      {n.image_url && <img src={imgSrc(n.image_url)} alt="" className="w-full h-full object-cover" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold truncate">{n.title}</p>
      <p className="text-xs text-[#4A4F6E] mt-1 font-mono">
        {formatDate(n.created_at)} · {n.category} · <span className={n.published ? "text-emerald-700" : "text-amber-700"}>{n.published ? "Publicerad" : "Utkast"}</span>
      </p>
    </div>
    <div className="flex gap-1">
      {n.published && <Link to={`/nyheter/${n.slug}`} target="_blank" className="p-2.5 rounded-full hover:bg-[#F3F2EC]" data-testid={`admin-news-view-${n.id}`}><ExternalLink className="w-4 h-4" /></Link>}
      <button onClick={() => onEdit(n)} className="p-2.5 rounded-full hover:bg-[#F3F2EC]" data-testid={`admin-news-edit-${n.id}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={() => onDelete(n)} className="p-2.5 rounded-full hover:bg-red-50 text-red-700" data-testid={`admin-news-delete-${n.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
);

export default function AdminNews() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [tab, setTab] = useState("news");

  const load = useCallback(() => {
    api.get("/admin/news").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/admin/news/${toDelete.id}`);
      toast.success("Nyheten togs bort");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-news-page">
      <header className="bg-white border-b border-[#E3E2DA]">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/"><LogoLight className="h-7" /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-[#3A3F5C]">{user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="admin-logout-button"><LogOut className="w-4 h-4" />Logga ut</button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((x) => (
            <button key={x.k} onClick={() => setTab(x.k)} data-testid={`admin-tab-${x.k}`} className={`rounded-full px-5 py-2 text-sm font-semibold border transition-colors ${tab === x.k ? "bg-[#070B2E] text-white border-[#070B2E]" : "bg-white border-[#D8D7CE] hover:border-[#070B2E]"}`}>{x.t}</button>
          ))}
        </div>
        {tab === "messages" ? (
          <>
            <h1 className="font-display text-4xl font-bold mb-8">Inkomna meddelanden</h1>
            <AdminMessages />
          </>
        ) : (
        <>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold">Nyheter</h1>
          </div>
          <button onClick={() => { setEditing(null); setOpen(true); }} className="inline-flex items-center gap-2 rounded-full bg-[#0B14A0] text-white px-6 py-3 font-semibold hover:bg-[#070B2E] transition-colors" data-testid="admin-create-news-button">
            <Plus className="w-4 h-4" />Ny nyhet
          </button>
        </div>
        <div className="space-y-3">
          {items.length === 0 && <p className="text-[#3A3F5C]" data-testid="admin-news-empty">Inga nyheter ännu.</p>}
          {items.map((n) => <Row key={n.id} n={n} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={setToDelete} />)}
        </div>
        </>
        )}
      </main>
      <NewsEditor open={open} onOpenChange={setOpen} item={editing} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort nyheten?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" tas bort permanent.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800" data-testid="admin-delete-confirm">Ta bort</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
